import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Contacts au chantier : surintendant, chargé de projet de l'entrepreneur
 * général, architecte, etc. Tout le monde assigné au chantier les voit ;
 * sur téléphone, un toucher sur le numéro lance l'appel et un toucher sur le
 * courriel ouvre un nouveau message. Seuls les administrateurs les modifient.
 */
export interface Contact {
  id: string
  chantier_id: string
  nom: string
  titre: string | null
  telephone: string | null
  courriel: string | null
  ordre: number
}

type Brouillon = { nom: string; titre: string; telephone: string; courriel: string }
const VIDE: Brouillon = { nom: '', titre: '', telephone: '', courriel: '' }

export function ContactsChantier({
  chantierId,
  modifiable
}: {
  chantierId: string
  modifiable: boolean
}) {
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [edition, setEdition] = useState<string | 'nouveau' | null>(null)
  const [brouillon, setBrouillon] = useState<Brouillon>(VIDE)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  const charger = useCallback(async () => {
    const { data, error } = await supabase
      .from('chantier_contacts')
      .select('*')
      .eq('chantier_id', chantierId)
      .order('ordre')
      .order('cree_le')
    if (error) setErreur(error.message)
    else setContacts((data as Contact[]) ?? [])
  }, [chantierId])

  useEffect(() => {
    void charger()
  }, [charger])

  function ouvrir(c: Contact | null) {
    setErreur(null)
    if (c) {
      setEdition(c.id)
      setBrouillon({
        nom: c.nom,
        titre: c.titre ?? '',
        telephone: c.telephone ?? '',
        courriel: c.courriel ?? ''
      })
    } else {
      setEdition('nouveau')
      setBrouillon(VIDE)
    }
  }

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault()
    if (!brouillon.nom.trim()) {
      setErreur('Le nom est obligatoire.')
      return
    }
    setOccupe(true)
    setErreur(null)
    const vide = (v: string) => v.trim() || null
    const valeurs = {
      nom: brouillon.nom.trim(),
      titre: vide(brouillon.titre),
      telephone: vide(brouillon.telephone),
      courriel: vide(brouillon.courriel)
    }
    const { error } =
      edition === 'nouveau'
        ? await supabase
            .from('chantier_contacts')
            .insert({ ...valeurs, chantier_id: chantierId, ordre: contacts?.length ?? 0 })
        : await supabase.from('chantier_contacts').update(valeurs).eq('id', edition!)
    setOccupe(false)
    if (error) {
      setErreur(error.message)
      return
    }
    setEdition(null)
    await charger()
  }

  async function supprimer(c: Contact) {
    if (!window.confirm(`Retirer ${c.nom} des contacts du chantier ?`)) return
    setOccupe(true)
    const { error } = await supabase.from('chantier_contacts').delete().eq('id', c.id)
    setOccupe(false)
    if (error) setErreur(error.message)
    setEdition(null)
    await charger()
  }

  const formulaire = (c: Contact | null) => (
    <form className="carte bande-nulle" onSubmit={(e) => void enregistrer(e)}>
      <label className="champ">
        <span>Nom *</span>
        <input
          value={brouillon.nom}
          onChange={(e) => setBrouillon({ ...brouillon, nom: e.target.value })}
          autoFocus
        />
      </label>
      <label className="champ">
        <span>Titre</span>
        <input
          value={brouillon.titre}
          onChange={(e) => setBrouillon({ ...brouillon, titre: e.target.value })}
          placeholder="Ex. : surintendant, chargé de projet (EG), architecte"
        />
      </label>
      <label className="champ">
        <span>Téléphone</span>
        <input
          type="tel"
          inputMode="tel"
          value={brouillon.telephone}
          onChange={(e) => setBrouillon({ ...brouillon, telephone: e.target.value })}
          placeholder="514 555-1234"
        />
      </label>
      <label className="champ">
        <span>Courriel</span>
        <input
          type="email"
          inputMode="email"
          value={brouillon.courriel}
          onChange={(e) => setBrouillon({ ...brouillon, courriel: e.target.value })}
        />
      </label>
      {erreur && (
        <p className="avis erreur" style={{ marginBottom: 12 }}>
          {erreur}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="bouton creux" disabled={occupe} onClick={() => setEdition(null)}>
          Annuler
        </button>
        <button className="bouton" disabled={occupe}>
          {occupe ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
      {c && (
        <button
          type="button"
          className="retour"
          style={{ marginTop: 12, textDecoration: 'underline' }}
          disabled={occupe}
          onClick={() => void supprimer(c)}
        >
          Retirer ce contact
        </button>
      )}
    </form>
  )

  return (
    <>
      <div className="sect">
        <span>
          Contacts au chantier <span className="code">{contacts?.length ?? ''}</span>
        </span>
        {modifiable && edition === null && (
          <button className="retour" onClick={() => ouvrir(null)}>
            + Ajouter
          </button>
        )}
      </div>

      <div className="liste">
        {contacts?.map((c) =>
          edition === c.id ? (
            <div key={c.id}>{formulaire(c)}</div>
          ) : (
            <div key={c.id} className="carte bande-nulle">
              <div className="entre">
                <div style={{ minWidth: 0 }}>
                  <p className="titre">{c.nom}</p>
                  {c.titre && (
                    <p className="meta">
                      <span>{c.titre}</span>
                    </p>
                  )}
                  <p className="meta" style={{ gap: 14 }}>
                    {c.telephone && (
                      <a className="lien-contact" href={`tel:${c.telephone.replace(/[^\d+]/g, '')}`}>
                        ☎ {c.telephone}
                      </a>
                    )}
                    {c.courriel && (
                      <a className="lien-contact" href={`mailto:${c.courriel}`}>
                        ✉ {c.courriel}
                      </a>
                    )}
                  </p>
                </div>
                {modifiable && edition === null && (
                  <button className="pastille p-ouvert" onClick={() => ouvrir(c)}>
                    Modifier
                  </button>
                )}
              </div>
            </div>
          )
        )}
        {edition === 'nouveau' && formulaire(null)}
        {contacts !== null && contacts.length === 0 && edition === null && (
          <p className="vide">
            Aucun contact pour ce chantier.
            {modifiable ? ' Ajoute le surintendant, le chargé de projet de l’EG, etc.' : ''}
          </p>
        )}
        {erreur && edition === null && <p className="avis erreur">{erreur}</p>}
      </div>
    </>
  )
}
